import React, { useEffect, useMemo, useState } from "react";
import BreadCrumsHeader from "../../common/BreadCrums";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from "@mui/material";
import Pagination from "../../common/TablePagination";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../common/Loader";
import SearchBar from "../../common/SearchBar";
import { getUserById, getUsers } from "../../../services/adminService";
import debounce from "lodash.debounce";
import DetailsModal from "../../common/DetailsModal";
import PopupContent from "./PopupContent";
import { Dropdown } from "antd";
import FilterPanel from "../../common/FilterPanel";
import { DatePicker } from "antd";
import { ReactComponent as DownArrow } from "../../../Assets/Icons/greyDownArrow.svg";
import { ReactComponent as FiltersIcon } from "../../../Assets/Icons/filtersIcon.svg";
// Removed fundraiser and merchant filters from User List
import RedeemedOffers from "./RedeemedOffers";
import FilterButton from "../../common/FilterButton";

const UserListDataTable = () => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state?.adminReducer?.loading);
  const [allUsers, setAllUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [count, setCount] = useState(0);
  const [statusToggled, setStatusToggled] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // Removed fundraiser and merchant search queries for User List
  const [open, setOpen] = useState(false);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [searching, setSearching] = useState(false);
  // Removed fundraiser and merchant lists for User List
  const [filterParams, setFilterParams] = useState({
    status: null,
    duration: null,
    startDate: null,
    endDate: null,
  });

  const DURATION_MAP = {
    "Today": "DAY",
    "This Week": "WEEK",
    "This Month": "MONTH",
    "This Year": "YEAR",
    "Custom Date": "CUSTOM",
  };
  const [customDate, setCustomDate] = useState([null, null]);

  // Redeemed offer modal state
  const [offerDetailsOpen, setOfferDetailsOpen] = useState(false);
  const [offerDetails, setOfferDetails] = useState(null);

  // Removed fundraiser and merchant debounced searches for User List

  // Removed fundraiser and merchant search handlers

  // Convert filterData to a computed value instead of state
  const filterData = useMemo(
    () => [
      {
        id: "status",
        title: "Subscription Status",
        options: ["Active", "InActive"],
        multiSelect: false,
        valueMap: {
          Active: "active",
          InActive: "inactive",
        },
      },
      {
        id: "duration",
        title: "Time Period",
        options: ["Today", "This Week", "This Month", "This Year", "Custom Date"],
        multiSelect: false,
        valueMap: DURATION_MAP,
        customContent: (selected) => {
          return selected === "Custom Date" ? (
            <div className="mt-3 flex flex-col gap-2">
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-medium text-gray-600">Start Date</label>
                <DatePicker
                  value={customDate?.[0]}
                  onChange={(date) => {
                    const newDates = [date, customDate?.[1]];
                    setCustomDate(newDates);
                  }}
                  style={{ width: '100%' }}
                  placeholder="Start Date"
                  format="YYYY-MM-DD"
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-medium text-gray-600">End Date</label>
                <DatePicker
                  value={customDate?.[1]}
                  onChange={(date) => {
                    const newDates = [customDate?.[0], date];
                    setCustomDate(newDates);
                  }}
                  style={{ width: '100%' }}
                  placeholder="End Date"
                  format="YYYY-MM-DD"
                />
              </div>
            </div>
          ) : null;
        }
      },
    ],
    [customDate]
  );

  // Create initial state from filter data
  const createInitialState = (filters) => {
    return filters.reduce((acc, filter) => {
      if (filter.multiSelect) {
        acc[filter.id] = Array.isArray(filter.defaultValue)
          ? filter.defaultValue
          : [];
      } else {
        acc[filter.id] = filter.defaultValue;
      }
      return acc;
    }, {});
  };

  const [selections, setSelections] = useState(createInitialState(filterData));

  const [fullUsers, setFullUsers] = useState([]);

  // Client-side pagination helper
  const updatePageSlice = (data, page) => {
    if (!data) return;
    const startIndex = (page - 1) * 50;
    const endIndex = startIndex + 50;
    setAllUsers(data.slice(startIndex, endIndex));
  };

  const getUsersData = async (query = "", silent = false) => {
    if (silent) setSearching(true);
    console.log('filterParams', filterParams)
    const payload = {
      // Fetch all (or significantly large number) to allow client-side sorting of recent users
      limit: 1000,
      search: query,
      role: "user",
      status: filterParams?.status,
      duration: filterParams?.duration,
      startDate: filterParams?.startDate,
      endDate: filterParams?.endDate,
      silent: silent,
    };
    console.log('payload', payload)

    try {
      const usersPromise = await dispatch(getUsers(payload))?.unwrap();
      if (usersPromise?.success === true) {
        const fetchedData = usersPromise?.data || [];

        // Sort descending by date (latest first)
        const sortedUsers = [...fetchedData].sort((a, b) => {
          const dateA = new Date(a.createdAt || 0);
          const dateB = new Date(b.createdAt || 0);
          return dateB - dateA;
        });

        setFullUsers(sortedUsers);
      }
    } finally {
      setSearching(false);
    }
  };

  // 1. Initial/Filter Load
  useEffect(() => {
    getUsersData(searchQuery);
  }, [statusToggled, filterParams]);

  // 2. Logic to filter locally when searching (Instant feel)
  useEffect(() => {
    if (!fullUsers) return;

    let filtered = [...fullUsers];

    if (searchQuery.trim()) {
      const term = searchQuery.toLowerCase().trim();
      filtered = filtered.filter((user) => {
        const name = (user?.name || "").toLowerCase();
        const email = (user?.email || "").toLowerCase();
        const joinedDate = formatDate(user?.createdAt).toLowerCase();

        return (
          name.includes(term) ||
          email.includes(term) ||
          joinedDate.includes(term)
        );
      });
    }

    setCount(filtered.length);
    setTotalPages(Math.ceil(filtered.length / 50) || 1);

    // Reset to page 1 on search
    const newPage = (searchQuery.trim() || filterParams.status || filterParams.duration) ? 1 : currentPage;
    setCurrentPage(newPage);

    updatePageSlice(filtered, newPage);
  }, [searchQuery, fullUsers]);

  // Handle page changes for client-side pagination
  useEffect(() => {
    if (fullUsers.length > 0) {
      updatePageSlice(fullUsers, currentPage);
    }
  }, [currentPage]);

  const items = [{ title: "Users" }, { title: "User List" }];

  const columns = [
    { name: "No." },
    { name: "Name" },
    { name: "Date Joined" },
    { name: "Email" },
    { name: "Sub. Amount" },
    { name: "Sub. Status" },
    { name: "Actions" },
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    // Scroll to top of user list when changing pages
    const container = document.getElementById('user-list-container');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth' });
    } else {
      // Fallback to window scroll
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  // Removed cleanup for fundraiser and merchant debounced searches

  const fetchUserDetails = async (userId) => {
    try {
      const result = await dispatch(getUserById({ id: userId, silent: true }))?.unwrap();
      if (result?.success) {
        setUserDetails(result.data);
      }
    } catch (e) { console.error(e); }
  };

  const openPopup = async (user) => {
    setUserDetails(null);
    setIsDetailOpen(true);
    if (user?._id) {
      fetchUserDetails(user._id);
    }
  };

  const closePopup = () => {
    setUserDetails(null);
    setIsDetailOpen(false);
  };

  // Removed fundraiser and merchant data fetching and effects for User List

  const handleSelectionChange = (filterId, value) => {
    console.log("handleSelectionChange called");
    console.log("filterData ++>", filterData);
    console.log("filterId ++>", filterId);
    console.log("value ++>", value);
    setSelections((prev) => ({
      ...prev,
      [filterId]: value,
    }));
  };

  const handleReset = () => {
    // handleCancel(); // Keep modal open on reset
    const initialState = createInitialState(filterData);
    setSelections(initialState);
    setFilterParams({
      status: null,
      duration: null,
      startDate: null,
      endDate: null,
    });
    setCustomDate([null, null]);
  };

  const handleApply = () => {
    handleCancel();
    console.log("Applying filters:", selections);

    const mappedFilterParams = {};

    filterData.forEach((filter) => {
      const selectedValue = selections[filter.id];
      if (
        selectedValue !== null &&
        selectedValue !== undefined &&
        selectedValue !== "" &&
        !(Array.isArray(selectedValue) && selectedValue.length === 0)
      ) {
        if (filter.id === "duration" && selectedValue === "Custom Date" && customDate && (customDate[0] || customDate[1])) {
          mappedFilterParams.duration = "CUSTOM";
          mappedFilterParams.startDate = customDate[0]?.format("YYYY-MM-DD");
          mappedFilterParams.endDate = customDate[1]?.format("YYYY-MM-DD");
        } else if (filter.valueMap) {
          mappedFilterParams[filter.id] = filter.multiSelect
            ? selectedValue.map((val) => filter.valueMap[val])
            : filter.valueMap[selectedValue];
        } else {
          mappedFilterParams[filter.id] = selectedValue;
        }
      }
    });
    console.log("Mapped Filter Params:", mappedFilterParams);
    setFilterParams(mappedFilterParams);
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleOpenChange = (flag) => {
    setOpen(flag);
  };

  // Callback for PopupContent
  const handleRedeemedOfferClick = (offer) => {
    setIsDetailOpen(false); // Close user modal
    setOfferDetails(offer);
    setOfferDetailsOpen(true); // Open offer details modal
  };

  console.log("userDetails", userDetails);
  return (
    <>
      {loading && <Loader />}
      {isDetailOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          animation: 'fadeIn 0.25s cubic-bezier(0.16, 1, 0.3, 1)'
        }}>
          <style>
            {`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes slideIn {
                from { transform: translateY(-20px) scale(0.95); opacity: 0; }
                to { transform: translateY(0) scale(1); opacity: 1; }
              }
            `}
          </style>
          <div style={{
            animation: 'slideIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
            width: '100%',
            display: 'flex',
            justifyContent: 'center'
          }}>
            <DetailsModal
              handleClose={closePopup}
              content={
                <PopupContent
                  userDetails={userDetails}
                  onRedeemedOfferClick={handleRedeemedOfferClick}
                />
              }
              popupClassName="p-6 w-[95%] max-w-[95vw] sm:w-[540px] md:w-[640px]"
            />
          </div>
        </div>
      )}
      {offerDetailsOpen && (
        <DetailsModal
          handleClose={() => {
            setOfferDetailsOpen(false);
            setIsDetailOpen(true); // Re-open parent modal
          }}
          content={<RedeemedOffers offerDetails={offerDetails} />}
          popupClassName="p-6 w-[95%] max-w-[95vw] sm:w-[600px] md:w-[728px]"
        />
      )}
      <div className="flex flex-col sm:flex-row justify-between col-span-12 gap-4">
        <BreadCrumsHeader heading="User List" items={items} search={true} />
      </div>
      <div className="w-full flex justify-between items-center mt-6 gap-3">
        <SearchBar
          parentDivClass="w-full sm:w-[60%] md:w-[40%] lg:w-[35%] xl:w-[30%]"
          divClassName="relative w-full"
          inputClassName="searchBar w-full p-3 pr-10 text-[14px] font-poppins font-[400] outline-none rounded-[5px] border border-[#D9D9D9] focus:border-[#00ADEF]"
          iconClassName="absolute right-[10px] top-1/2 -translate-y-1/2 w-[18px] h-[18px]"
          placeholder="Search"
          value={searchQuery}
          handleChange={(e) => handleSearchChange(e.target.value)}
          loading={searching}
        />
        <Dropdown
          overlay={
            <FilterPanel
              filterData={filterData}
              selections={selections}
              handleApply={handleApply}
              handleCancel={handleCancel}
              handleReset={handleReset}
              handleSelectionChange={handleSelectionChange}
            />
          }
          trigger={["click"]}
          open={open}
          onOpenChange={handleOpenChange}
          placement="bottomRight"
          overlayStyle={{
            minWidth: "280px",
            width: "max-content",
            maxWidth: "90vw",
            maxHeight: "80vh",
            zIndex: 1000,
          }}
          overlayClassName="filter-dropdown-overlay"
        >
          <FilterButton isOpen={open} />
        </Dropdown>
      </div>
      <div id="user-list-container" className="col-span-12 mt-8">
        {/* Desktop Table View */}
        <TableContainer style={{ width: "100%", overflowX: "auto" }} className="hidden md:block">
          <Table
            sx={{
              minWidth: "800px",
              borderCollapse: "separate",
              borderSpacing: "0px 8px",
            }}
            aria-label="simple table"
          >
            <TableHead className="bg-[#00ADEF]">
              <TableRow
                className="h-[50px]"
                sx={{ boxShadow: "0px 4px 4px 0px #00000040" }}
              >
                {columns.map((column, index) => (
                  <TableCell key={index} className="!text-[#FFFFFF]">
                    {column.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {allUsers.map((user, index) => (
                <TableRow
                  key={index}
                  className="h-[50px]"
                  sx={{
                    boxShadow: "0px 4px 4px 0px #00000040",
                    backgroundColor:
                      (index + 1) % 2 !== 0 ? "#F5F5F5" : "#FFFFFF",
                  }}
                >
                  <TableCell>{(currentPage - 1) * 50 + index + 1}</TableCell>
                  <TableCell>{user?.name}</TableCell>
                  <TableCell>{formatDate(user?.createdAt)}</TableCell>
                  <TableCell>{user?.email}</TableCell>
                  <TableCell>${user?.subscriptionAmount || "0"}</TableCell>
                  <TableCell>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-medium border-[0.5px] ${user?.subscriptionStatus
                        ? "bg-[#50CD8933] text-[#303033] border-[#50CD89]"
                        : "bg-[#BABABA33] text-[#303033] border-[#B1B1B1]"
                        }`}
                    >
                      {user?.subscriptionStatus ? "Active" : "Inactive"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <button
                      className={`flex justify-center items-center border rounded-[6px] p-3 h-[34px] border-[#00ADEF] text-[#00ADEF]`}
                      onClick={() => openPopup(user)}
                    >
                      <span className="font-[600] text-[14px] font-poppins leading-[22px]">
                        View Details
                      </span>
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Mobile Card View */}
        <div className="block md:hidden space-y-4">
          {allUsers.map((user, index) => (
            <div
              key={index}
              className="bg-white rounded-[10px] p-4 border border-[#E0E0E0] shadow-sm flex flex-col gap-3"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-[16px] font-[600] text-[#303030] font-poppins truncate max-w-[200px]">
                    {user?.name || "N/A"}
                  </h3>
                  <p className="text-[12px] font-[400] text-[#767676] font-poppins">
                    {user?.email || "N/A"}
                  </p>
                </div>
                <span
                  className={`px-2 py-0.5 rounded-full text-[11px] font-medium border-[0.5px] ${user?.subscriptionStatus
                    ? "bg-[#50CD8933] text-[#303033] border-[#50CD89]"
                    : "bg-[#BABABA33] text-[#303033] border-[#B1B1B1]"
                    }`}
                >
                  {user?.subscriptionStatus ? "Active" : "Inactive"}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[13px] font-poppins text-[#303030]">
                <div className="flex flex-col">
                  <span className="text-[#8C8C8C] text-[11px]">Joined Date</span>
                  <span className="truncate">
                    {formatDate(user?.createdAt)}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="text-[#8C8C8C] text-[11px]">Sub. Amount</span>
                  <span className="truncate font-[600] text-[#00ADEF]">
                    ${user?.subscriptionAmount || "0"}
                  </span>
                </div>
              </div>

              <button
                className="w-full mt-2 flex justify-center items-center border border-[#00ADEF] rounded-[6px] py-2 bg-[#F0FBFF] hover:bg-[#E0F7FA] transition-colors"
                onClick={() => openPopup(user)}
              >
                <span className="font-[600] text-[13px] text-[#00ADEF] font-poppins">
                  View Details
                </span>
              </button>
            </div>
          ))}
        </div>

        <Pagination
          totalPages={totalPages}
          handlePageChange={handlePageChange}
        />
      </div>
    </>
  );
};

export default UserListDataTable;
